import React, { useState, useCallback, Fragment, useMemo, useEffect, useContext } from 'react';
import ConfirmContext from "./ConfirmContext";
import ConfirmationDialog from "./ConfirmationDialog";

const DEFAULT_OPTIONS = {
  title: "Are you sure?",
  description: "",
  content: null,
  Form: null,
  FormProps: null,
  onSubmit: null,
  confirmAction: null,
  cancelAction: null,
  closeAction: null,
  confirmationText: "Ok",
  cancellationText: "Cancel",
  dialogProps: {},
  dialogActionsProps: {
    sx: { flexWrap: "wrap" },
  },
  dialogActionsContent: null,
  confirmationButtonProps: {
    variant: 'contained',
  },
  cancellationButtonProps: {
    variant: 'outlined',
  },
  titleProps: {
    id: 'confirm-dialog-title',
  },
  contentProps: {
    id: 'confirm-dialog-description',
  },
  allowClose: true,
  confirmationKeywordTextFieldProps: {},
  hideCancelButton: false,
  buttonOrder: ["cancel", "value", "confirm"],
  valueButtons: [],
  acknowledgement: false,
  acknowledgementFormControlLabelProps: {},
  acknowledgementCheckboxProps: {},
};

const buildOptions = (defaultOptions, options) => {
  const dialogProps = {
    ...(defaultOptions.dialogProps || DEFAULT_OPTIONS.dialogProps),
    ...(options.dialogProps || {}),
  };
  const dialogActionsProps = {
    ...(defaultOptions.dialogActionsProps ||
      DEFAULT_OPTIONS.dialogActionsProps),
    ...(options.dialogActionsProps || {}),
  };
  const confirmationButtonProps = {
    ...(defaultOptions.confirmationButtonProps ||
      DEFAULT_OPTIONS.confirmationButtonProps),
    ...(options.confirmationButtonProps || {}),
  };
  const cancellationButtonProps = {
    ...(defaultOptions.cancellationButtonProps ||
      DEFAULT_OPTIONS.cancellationButtonProps),
    ...(options.cancellationButtonProps || {}),
  };
  const titleProps = {
    ...(defaultOptions.titleProps || DEFAULT_OPTIONS.titleProps),
    ...(options.titleProps || {}),
  };
  const contentProps = {
    ...(defaultOptions.contentProps || DEFAULT_OPTIONS.contentProps),
    ...(options.contentProps || {}),
  };
  const confirmationKeywordTextFieldProps = {
    ...(defaultOptions.confirmationKeywordTextFieldProps ||
      DEFAULT_OPTIONS.confirmationKeywordTextFieldProps),
    ...(options.confirmationKeywordTextFieldProps || {}),
  };
  const acknowledgementFormControlLabelProps = {
    ...(defaultOptions.acknowledgementFormControlLabelProps ||
      DEFAULT_OPTIONS.acknowledgementFormControlLabelProps),
    ...(options.acknowledgementFormControlLabelProps || {}),
  };
  const acknowledgementCheckboxProps = {
    ...(defaultOptions.acknowledgementCheckboxProps ||
      DEFAULT_OPTIONS.acknowledgementCheckboxProps),
    ...(options.acknowledgementCheckboxProps || {}),
  };

  return {
    ...DEFAULT_OPTIONS,
    ...defaultOptions,
    ...options,
    dialogProps,
    dialogActionsProps,
    confirmationButtonProps,
    cancellationButtonProps,
    titleProps,
    contentProps,
    confirmationKeywordTextFieldProps,
    acknowledgementFormControlLabelProps,
    acknowledgementCheckboxProps,
  };
};

let confirmGlobal;

const ConfirmProvider = ({ children, defaultOptions = {} }) => {
  // State that we clear on close (to avoid dangling references to resolve and
  // reject). If this is null, the dialog is closed.
  const [state, setState] = useState(null);
  // Options for rendering the dialog, which aren't reset on close so that we
  // keep rendering the same modal during close animation
  const [options, setOptions] = useState({});
  const [key, setKey] = useState(0);

  const confirmBase = useCallback((parentId, options = {}) => {
    return new Promise((resolve, reject) => {
      setKey((key) => key + 1);
      setOptions(options);
      setState({resolve, reject, parentId});
    });
  }, []);

  const closeOnParentUnmount = useCallback((parentId) => {
    setState((state) => {
      if (state && state.parentId === parentId) {
        return null;
      } else {
        return state;
      }
    });
  }, []);

  const handleClose = useCallback(() => {
    if (options.closeAction) {
      options.closeAction();
    }
    setState(null);
  }, [options]);

  const handleCancel = useCallback(() => {
    if (options.cancelAction) {
      options.cancelAction();
    }
    setState((state) => {
      state && state.reject();
      return null;
    });
  }, [options]);

  const handleConfirm = useCallback(() => {
    // confirmAction - external function to call to trigger potential resolve
    // resolver - passed to confirmAction so that it can call it at a future time
    if (options.confirmAction) {
      options.confirmAction(() => {
        setState((state) => {
          state && state.resolve();
          return null;
        });
      });
    } else {
      setState((state) => {
        state && state.resolve();
        return null;
      });
    }
  }, [options]);

  const handleValue = useCallback((value) => {
    setState((state) => {
      state && state.resolve(value);
      return null;
    });
  }, []);

  confirmGlobal = useCallback((options) => {
    return confirmBase("global", options);
  });

  const builtOptions = buildOptions(defaultOptions, options ?? {});

  return (
    <Fragment>
      <ConfirmContext.Provider value={{ confirmBase, closeOnParentUnmount }}>
        {children}
      </ConfirmContext.Provider>
      <ConfirmationDialog
        key={key}
        open={state !== null}
        options={builtOptions}
        setOptions={setOptions}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onValue={handleValue}
      />
    </Fragment>
  );
};

export default ConfirmProvider;
export { confirmGlobal as confirm };
